import { SVGFC } from '@repo/toolkit/types'
import { DeleteIcon } from '@repo/ui/icons'
import { ButtonHTMLAttributes } from 'react'

export type ValidationButtonVariant = 'primary' | 'ghost' | 'destructive'

type PassValidationButton<T> = (values: T) => void
type BypassValidationButton = () => void

type ValidationButtonBaseProps = {
  label: string
  type: ButtonHTMLAttributes<HTMLButtonElement>['type']
  variant?: ValidationButtonVariant
  disabled?: boolean
  isLoading?: boolean
  icon?: SVGFC
  className?: string
}

// If bypassValidation is true, the button will not trigger form validation before executing the action
// And the action will be called without any argument because the form values are not needed, it will be an extra button like "Cancel"
export type ValidationButton<T> =
  | (ValidationButtonBaseProps & {
      bypassValidation: true
      action: BypassValidationButton
    })
  | (ValidationButtonBaseProps & {
      bypassValidation?: false
      action: PassValidationButton<T>
    })

type CreateButtonsConfig<T> = {
  confirmText?: string
  cancelText?: string
  onConfirm: PassValidationButton<T>
  onCancel: BypassValidationButton
  isEnglish?: boolean
}
const getLabel = (label: string, labelEn: string, isEnglish?: boolean) => {
  return isEnglish ? labelEn : label
}

export const twoButtonsConfig = <T>({
  onCancel,
  onConfirm: onCreate,
  confirmText,
  cancelText,
  isEnglish
}: CreateButtonsConfig<T>): ValidationButton<T>[] => {
  return [
    {
      type: 'button',
      label: getLabel(cancelText ?? 'Retour', 'Close', isEnglish),
      variant: 'ghost',
      bypassValidation: true,
      action: onCancel
    },
    {
      type: 'submit',
      label: getLabel(confirmText ?? 'Valider', confirmText ?? 'Save', isEnglish),
      variant: 'primary',
      action: onCreate
    }
  ]
}

type UpdateButtonsConfig<T> = {
  onUpdate?: PassValidationButton<T>
  onCancel?: BypassValidationButton
  onUpdateThenCreate?: PassValidationButton<T>
  onUpdateThenList?: PassValidationButton<T>
  isEnglish?: boolean
}

export const updateButtonsConfig = <T>({
  onCancel,
  onUpdate,
  onUpdateThenCreate,
  onUpdateThenList,
  isEnglish
}: UpdateButtonsConfig<T>): ValidationButton<T>[] => {
  const buttons: ValidationButton<T>[] = []

  if (onCancel) {
    buttons.push({
      type: 'button',
      label: getLabel('Retour', 'Close', isEnglish),
      variant: 'ghost',
      bypassValidation: true,
      action: onCancel
    })
  }

  if (onUpdate) {
    buttons.push({
      type: 'submit',
      label: getLabel('Enregistrer', 'Save', isEnglish),
      action: onUpdate
    })
  }

  if (onUpdateThenCreate) {
    buttons.push({
      type: 'button',
      label: getLabel('Enregistrer +1', 'Save +1', isEnglish),
      action: onUpdateThenCreate
    })
  }

  if (onUpdateThenList) {
    buttons.push({
      type: 'button',
      label: getLabel('Enregistrer et retour', 'Save and return to the list', isEnglish),
      action: onUpdateThenList
    })
  }

  return buttons
}

type ModalButtonConfig<T> = {
  onConfirm?: PassValidationButton<T>
  onCancel?: BypassValidationButton
  onDelete?: BypassValidationButton
  disabled?: boolean
  isLoading?: boolean
  confirmText?: string
  cancelText?: string
  deleteText?: string
  isDeleteLoading?: boolean
  isEnglish?: boolean
}

export const modalButtonConfig = <T>({
  onCancel,
  onConfirm,
  onDelete,
  disabled = false,
  confirmText = 'Valider',
  cancelText = 'Fermer',
  deleteText = 'Supprimer',
  isLoading = false,
  isDeleteLoading = false,
  isEnglish
}: ModalButtonConfig<T>): ValidationButton<T>[] => {
  const buttons: ValidationButton<T>[] = []

  // Add the cancel button if onCancel is defined
  if (onCancel) {
    buttons.push({
      type: 'button',
      label: getLabel(cancelText ?? 'Fermer', 'Close', isEnglish),
      variant: 'ghost',
      bypassValidation: true,
      action: onCancel
    })
  }
  // Add the delete button if onDelete is defined
  if (onDelete) {
    buttons.push({
      type: 'button',
      label: getLabel(deleteText ?? 'Supprimer', 'Delete', isEnglish),
      variant: 'destructive',
      icon: DeleteIcon,
      isLoading: isDeleteLoading,
      bypassValidation: true,
      action: onDelete
    })
  }

  // Add the confirm button if onConfirm is defined
  if (onConfirm) {
    buttons.push({
      type: 'submit',
      label: getLabel(confirmText ?? 'Valider', 'Confirm', isEnglish),
      variant: 'primary',
      action: onConfirm,
      disabled,
      isLoading
    })
  }

  return buttons
}

export const getSubmitAction = <T>(config: ValidationButton<T>[]) => {
  const submitButton = config.find(({ type }) => type === 'submit')
  if (!submitButton) return () => {} // we return an empty function if there is no submit button

  return submitButton.action
}
